import { Eye, EyeOff } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { AuthCardLayout } from "../components/AuthCardLayout";
import { betterAuthRequest } from "../lib/auth";
import { buildAuthCallbackUrl, buildForgotPasswordPath } from "../lib/invitation-flow";
import { useAuth } from "../hooks/useAuth";
import { APP_HOME_PATH } from "../routes";

type AuthMode = "signin" | "signup";

export function LoginPage() {
  const { user, login, signup } = useAuth();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectPath = searchParams.get("redirect") || APP_HOME_PATH;
  const wasVerified = searchParams.get("verified") === "1";
  const wasPasswordReset = searchParams.get("reset") === "1";
  const inviteEmail = searchParams.get("email")?.trim().toLowerCase() ?? "";
  const inviteSignin = searchParams.get("invite") === "1";
  const signupFlow = searchParams.get("signup") === "1";
  const signupEmail = searchParams.get("signupEmail")?.trim().toLowerCase() ?? "";
  const pendingOrganizationName = searchParams.get("pendingOrg")?.trim() ?? "";
  const prefilledEmail = inviteEmail || signupEmail;

  const [mode, setMode] = useState<AuthMode>("signin");
  const [name, setName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const forgotPasswordPath = useMemo(
    () =>
      buildForgotPasswordPath({
        redirectPath,
        email: prefilledEmail || email.trim(),
      }),
    [email, prefilledEmail, redirectPath],
  );

  useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  useEffect(() => {
    if (wasPasswordReset) {
      setMode("signin");
      setNotice("Password updated. Sign in with your new password.");
      return;
    }

    if (wasVerified) {
      setMode("signin");
      setNotice("Email verified. Sign in to continue.");
      return;
    }

    if (inviteSignin) {
      setMode("signin");
      setNotice("This email already has an account. Sign in to continue with the invitation.");
    }
  }, [inviteSignin, wasPasswordReset, wasVerified]);

  if (user) {
    return <Navigate to={redirectPath} replace />;
  }

  const resendVerificationEmail = async (targetEmail: string) => {
    await betterAuthRequest("/send-verification-email", {
      method: "POST",
      body: JSON.stringify({
        email: targetEmail.trim().toLowerCase(),
        callbackURL: buildAuthCallbackUrl(window.location.origin, {
          redirectPath,
          signupEmail: signupFlow ? targetEmail.trim().toLowerCase() : undefined,
          signupOrganizationName: signupFlow ? pendingOrganizationName : undefined,
        }),
      }),
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);

    try {
      if (mode === "signup") {
        const result = await signup({
          name: name.trim(),
          email: email.trim(),
          password,
          organizationName: organizationName.trim(),
          redirectPath,
        });

        if (result.status === "verification-required") {
          setMode("signin");
          setPassword("");
          setNotice(
            result.emailSent
              ? `Verification email sent to ${result.email}. Verify your email, then sign in to continue.`
              : `Verify ${result.email} first, then sign in to continue. If you do not see a verification email, try signing in again and we will resend it.`,
          );
          return;
        }

        if (result.status === "signin-required") {
          setMode("signin");
          setPassword("");
          setNotice(
            `No session was created for ${result.email}. If this account is new, verify your email first. Otherwise sign in or reset the password to continue.`,
          );
          return;
        }
      } else {
        await login(email.trim(), password, { redirectPath });
      }

      window.location.assign(redirectPath);
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message
          : mode === "signup"
            ? "Unable to create account"
            : "Unable to log in";

      if (message === "Email not verified") {
        try {
          await resendVerificationEmail(email);
          setMode("signin");
          setNotice(
            `Verification email sent to ${email.trim().toLowerCase()}. Verify your email, then sign in to continue.`,
          );
          return;
        } catch {
          setError("Email not verified. Unable to resend the verification email.");
          return;
        }
      }

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCardLayout
      heroEyebrow="Workspace access"
      heroTitle="Access Datascope."
      heroDescription="Sign in to continue working, or create a new account and your first organization to start configuring agents and approved database access."
      formEyebrow="Authentication"
      formTitle={mode === "signup" ? "Create account" : "Sign in"}
      formDescription={
        mode === "signup"
          ? "Create your account and first organization."
          : "Use your existing account to continue."
      }
    >
      <div className="mt-8 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
        <button
          type="button"
          onClick={() => {
            setMode("signin");
            setError(null);
            setNotice(null);
          }}
          className={`rounded-full px-5 py-2 text-sm font-medium transition ${
            mode === "signin"
              ? "bg-primary text-white"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          Sign in
        </button>
        <button
          type="button"
          onClick={() => {
            setMode("signup");
            setError(null);
            setNotice(null);
          }}
          className={`rounded-full px-5 py-2 text-sm font-medium transition ${
            mode === "signup"
              ? "bg-primary text-white"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          Create account
        </button>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        {mode === "signup" ? (
          <>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Name</span>
              <input
                type="text"
                value={name}
                disabled={submitting}
                onChange={(event) => setName(event.target.value)}
                className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary"
                placeholder="Your name"
                required
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                Organization
              </span>
              <input
                type="text"
                value={organizationName}
                disabled={submitting}
                onChange={(event) => setOrganizationName(event.target.value)}
                className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary"
                placeholder="Acme Analytics"
                required
              />
            </label>
          </>
        ) : null}

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">
            {mode === "signup" ? "Email" : "Email or username"}
          </span>
          <input
            type={mode === "signup" ? "email" : "text"}
            value={email}
            disabled={submitting}
            onChange={(event) => setEmail(event.target.value)}
            className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary"
            placeholder={mode === "signup" ? "satya@company.com" : "satya or satya@company.com"}
            required
          />
        </label>

        <label className="block">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Password</span>
            {mode === "signin" ? (
              <Link
                to={forgotPasswordPath}
                className="text-sm font-medium text-slate-600 underline-offset-4 hover:text-slate-900 hover:underline"
              >
                Forgot password?
              </Link>
            ) : null}
          </div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              minLength={8}
              disabled={submitting}
              onChange={(event) => setPassword(event.target.value)}
              className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 shadow-sm outline-none transition focus:border-primary"
              placeholder={mode === "signup" ? "Create a password" : "Enter your password"}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-slate-600"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </label>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {notice ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {notice}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-[#005f8a] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? mode === "signup"
              ? "Creating account..."
              : "Signing in..."
            : mode === "signup"
              ? "Create account"
              : "Sign in"}
        </button>
      </form>
    </AuthCardLayout>
  );
}
